import React, { Component, Fragment } from 'react';
import { PageHeader, Table, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Loader from '../components/atoms/Loader';
import OutcomePill from '../components/DriverFix/OutcomePill';
import getFixTypeLabel, { FIX_TYPE_OPTIONS } from '../utils/fixTypeLabels';
import { GetAllDriverFixActions } from '../libs/GraphQL';

const OUTCOME_OPTIONS = [
  { value: 'resolved', label: 'Resolved' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'abandoned', label: 'Abandoned' },
];

// HEARTBEAT_RESULT events encode the outcome in data.title as
// "Heartbeat outcome: <value>" (driver-app/src/products/DriverFixes.js:610).
const HEARTBEAT_OUTCOME_REGEX = /^Heartbeat outcome:\s+(\S+)/;

const parseHeartbeatOutcome = (title) => {
  if (!title) return null;
  const m = String(title).match(HEARTBEAT_OUTCOME_REGEX);
  return m ? m[1] : null;
};

const heartbeatOutcomeBucket = (outcome) => {
  if (outcome === 'success' || outcome === 'success_with_issue')
    return 'resolved';
  // Backend re-check still saw the fault. Bucketed with driver-asserted
  // escalations (COMPLETION_FEEDBACK hadIssue=true) — both mean ops follow-up.
  if (outcome === 'issue_detected') return 'escalated';
  if (
    outcome === 'connection_error_final' ||
    outcome === 'connection_skipped'
  ) {
    return 'unverified';
  }
  return null;
};

// "All time" is bounded at 365 days — the existing GetDriverAction resolver
// requires one createdAt per call, and a year of fan-out is the practical cap.
const DATE_RANGES = [
  { value: 1, label: 'Last day' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'All time' },
];

const FETCH_CONCURRENCY = 8;

async function fetchEventsAcrossDays(daysBack) {
  const dates = [];
  const today = moment();
  for (let i = 0; i < daysBack; i += 1) {
    dates.push(today.clone().subtract(i, 'days').format('YYYY-MM-DD'));
  }
  const events = [];
  for (let i = 0; i < dates.length; i += FETCH_CONCURRENCY) {
    const batch = dates.slice(i, i + FETCH_CONCURRENCY);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.all(
      batch.map((d) => GetAllDriverFixActions(d, 'DriverFix')),
    );
    results.forEach((arr) => events.push(...arr));
  }
  return events;
}

const safeParse = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};

const eventTimestamp = (event, data) => {
  if (data && data.timestamp) return Number(data.timestamp);
  if (event && event._lastModified) return Number(event._lastModified);
  return null;
};

const formatDateTime = (ms) => (ms ? moment(ms).format('MMM D, h:mm A') : '—');

const formatDuration = (startMs, endMs) => {
  if (!startMs || !endMs) return '—';
  const mins = Math.max(0, Math.round((endMs - startMs) / 60000));
  if (mins < 1) return '<1 min';
  return `${mins} min`;
};

// Task titles are stored on the driver-app as "{order}#{title}" so the
// receiving event handler can recover the task index. Strip the prefix
// when rendering for humans.
const stripStepNumber = (title) =>
  title ? String(title).replace(/^\d+#/, '') : title;

function renderActionContent(data) {
  const taskType = data.taskType;
  const notes = data.notes;
  const parts = [];
  const displayTitle = stripStepNumber(data.title);
  if (displayTitle) parts.push(<strong key="title">{displayTitle}</strong>);
  if (notes) {
    parts.push(<span key="notes"> &mdash; &ldquo;{notes}&rdquo;</span>);
  }
  if (taskType === 'COMPLETION_FEEDBACK' && data.hadIssue !== undefined) {
    parts.push(
      <span key="hadIssue" style={{ color: '#666' }}>
        {' '}
        (hadIssue: {String(data.hadIssue)})
      </span>,
    );
  }
  if (data.s3Url) {
    parts.push(
      <span key="photo" style={{ color: '#666' }}>
        {' '}
        [photo: {data.s3Url}]
      </span>,
    );
  }
  return parts.length ? parts : <em style={{ color: '#888' }}>—</em>;
}

// Workflow-level events (WORKFLOW_SKIPPED, WORKFLOW_COMPLETED) live outside
// the per-fix bucket because they emit with a comma-joined fixType. Append
// them to each per-fix timeline so the expanded row shows how the surrounding
// workflow ended.
function timelineEventsForRow(rowEvents, workflowBucket) {
  const base = rowEvents || [];
  if (!workflowBucket) return base;
  const extras = [];
  if (workflowBucket.skipped) extras.push(workflowBucket.skipped);
  if (workflowBucket.completed) extras.push(workflowBucket.completed);
  return base.concat(extras).sort((a, b) => (a.ts || 0) - (b.ts || 0));
}

function renderTimeline(events) {
  if (!events || !events.length) {
    return (
      <div style={{ padding: '8px 16px', color: '#888' }}>
        No actions recorded.
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 20px', background: '#f9f9f9' }}>
      <div
        style={{
          fontWeight: 600,
          marginBottom: '8px',
          color: '#444',
          fontSize: '0.9em',
        }}
      >
        Driver actions
      </div>
      <table style={{ width: '100%', fontSize: '0.9em' }}>
        <tbody>
          {events.map((e, idx) => (
            <tr
              // eslint-disable-next-line react/no-array-index-key
              key={`${e.ts || 0}-${e.data.taskType}-${idx}`}
              style={{ borderTop: idx ? '1px solid #eee' : 'none' }}
            >
              <td
                style={{
                  width: '140px',
                  whiteSpace: 'nowrap',
                  color: '#666',
                  padding: '4px 8px',
                }}
              >
                {formatDateTime(e.ts)}
              </td>
              <td
                style={{
                  width: '200px',
                  padding: '4px 8px',
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                  color: '#555',
                }}
              >
                {e.data.taskType || '—'}
              </td>
              <td style={{ padding: '4px 8px' }}>
                {renderActionContent(e.data)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default class DriverFixesList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      daysBack: 7,
      rows: [],
      eventsByKey: {},
      workflowEventsByProcessId: {},
      expandedKeys: {},
      isLoading: false,
      error: null,
      filters: {
        location: '',
        driver: '',
        fixType: '',
        outcome: '',
      },
    };
  }

  componentDidMount() {
    this.fetchEvents();
  }

  fetchEvents = async () => {
    const { daysBack } = this.state;
    this.setState({ isLoading: true, error: null });
    try {
      // All DriverFix flow events share eventType="DriverFix"; the lifecycle
      // marker is in data.taskType. Fan out one query per day in the range
      // then group every event by (stockingProcessId, fixType) so the
      // expanded detail row can show the full driver action trace.
      const events = await fetchEventsAcrossDays(daysBack);

      // Per-fix events (WORKFLOW_STARTED, HEARTBEAT_*, COMPLETION_FEEDBACK)
      // emit with fixType=currentFix.fixType, so they group cleanly by
      // (stockingProcessId, fixType). Workflow-level events (WORKFLOW_COMPLETED,
      // WORKFLOW_SKIPPED) emit with fixType = fixes.join(', ') — index them
      // separately by stockingProcessId so the comma-joined fixType doesn't
      // bury them in their own orphan bucket.
      const eventsByKey = {};
      const workflowEventsByProcessId = {};
      events.forEach((event) => {
        const data = safeParse(event.data);
        if (!data || !data.stockingProcessId) return;
        const ts = eventTimestamp(event, data);
        if (
          data.taskType === 'WORKFLOW_COMPLETED' ||
          data.taskType === 'WORKFLOW_SKIPPED'
        ) {
          const bucket =
            workflowEventsByProcessId[data.stockingProcessId] || {};
          // handleSkip in driver-app emits WORKFLOW_SKIPPED then a follow-up
          // WORKFLOW_COMPLETED with notes='skipped'. Both belong in the
          // skipped slot — the completed slot is only for natural finishes.
          const isSkip =
            data.taskType === 'WORKFLOW_SKIPPED' || data.notes === 'skipped';
          const slot = isSkip ? 'skipped' : 'completed';
          if (!bucket[slot] || (ts || 0) > (bucket[slot].ts || 0)) {
            bucket[slot] = { event, data, ts };
          }
          workflowEventsByProcessId[data.stockingProcessId] = bucket;
          return;
        }
        const key = `${data.stockingProcessId}#${data.fixType}`;
        if (!eventsByKey[key]) eventsByKey[key] = [];
        eventsByKey[key].push({ event, data, ts });
      });
      Object.values(eventsByKey).forEach((arr) =>
        arr.sort((a, b) => (a.ts || 0) - (b.ts || 0)),
      );

      const rows = Object.entries(eventsByKey)
        .map(([key, arr]) => {
          const anchor = arr.find(
            (e) => e.data.taskType === 'WORKFLOW_STARTED',
          );
          if (!anchor) return null;
          const heartbeatResult = arr.find(
            (e) => e.data.taskType === 'HEARTBEAT_RESULT',
          );
          const completion = arr.find(
            (e) => e.data.taskType === 'COMPLETION_FEEDBACK',
          );
          const heartbeatCheck = arr.find(
            (e) => e.data.taskType === 'HEARTBEAT_CHECK',
          );
          const workflow =
            workflowEventsByProcessId[anchor.data.stockingProcessId];

          let outcome = 'abandoned';
          let endMs = null;
          if (heartbeatResult) {
            endMs = heartbeatResult.ts;
            const parsed = parseHeartbeatOutcome(heartbeatResult.data.title);
            outcome = heartbeatOutcomeBucket(parsed) || 'unverified';
          } else if (completion) {
            endMs = completion.ts;
            outcome =
              completion.data.hadIssue === true ? 'escalated' : 'resolved';
          } else if (workflow?.skipped) {
            endMs = workflow.skipped.ts;
            outcome = 'skipped';
          } else if (heartbeatCheck) {
            // HEARTBEAT_CHECK without a paired RESULT. The RESULT mutation is
            // fire-and-forget on driver-app, so a missing RESULT is common
            // even when the driver completed the flow. If the workflow
            // finished after the check, treat it as resolved (same rule as
            // the workflow-only terminal below). Without a workflow finish,
            // the driver bailed at the heartbeat screen → abandoned.
            endMs = heartbeatCheck.ts;
            outcome = 'abandoned';
            if (
              workflow?.completed &&
              (workflow.completed.ts || 0) > (heartbeatCheck.ts || 0)
            ) {
              endMs = workflow.completed.ts;
              outcome = 'resolved';
            }
          } else if (workflow?.completed) {
            // Workflow finished with no per-fix terminal event. The fix's
            // task list does not end in CONFIRM_FIX (and the completion
            // prompt either didn't fire or its mutation dropped), so
            // WORKFLOW_COMPLETED itself is the legitimate terminal — the
            // driver finished the flow as designed.
            endMs = workflow.completed.ts;
            outcome = 'resolved';
          }

          return {
            rowKey: key,
            startMs: anchor.ts,
            locationId: anchor.event.locationId || anchor.data.locationId || '',
            driver: anchor.data.driver || '',
            fixType: anchor.data.fixType || '',
            stockingProcessId: anchor.data.stockingProcessId || '',
            outcome,
            duration: formatDuration(anchor.ts, endMs),
          };
        })
        .filter(Boolean)
        .sort((a, b) => (b.startMs || 0) - (a.startMs || 0));

      this.setState({
        rows,
        eventsByKey,
        workflowEventsByProcessId,
        expandedKeys: {},
        isLoading: false,
      });
    } catch (e) {
      console.error('Failed to load DriverFix events', e);
      this.setState({
        error:
          'Failed to load DriverFix events. Check your network and permissions.',
        isLoading: false,
      });
    }
  };

  handleRowClick = (rowKey) => {
    this.setState((prev) => {
      const next = { ...prev.expandedKeys };
      if (next[rowKey]) {
        delete next[rowKey];
      } else {
        next[rowKey] = true;
      }
      return { expandedKeys: next };
    });
  };

  handleRangeChange = (event) => {
    this.setState({ daysBack: Number(event.target.value) }, this.fetchEvents);
  };

  handleFilterChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => ({
      filters: { ...prev.filters, [name]: value },
    }));
  };

  applyFilters(rows) {
    const { location, driver, fixType, outcome } = this.state.filters;
    const locLc = location.trim().toLowerCase();
    const drvLc = driver.trim().toLowerCase();
    return rows.filter((row) => {
      if (locLc && !String(row.locationId).toLowerCase().includes(locLc)) {
        return false;
      }
      if (drvLc && !row.driver.toLowerCase().includes(drvLc)) {
        return false;
      }
      if (fixType && row.fixType !== fixType) return false;
      if (outcome && row.outcome !== outcome) return false;
      return true;
    });
  }

  renderFilters() {
    const { daysBack, filters } = this.state;
    return (
      <div className="row" style={{ marginBottom: '1em' }}>
        <Col xs={12} sm={2}>
          <label htmlFor="dfx-range" style={{ width: '100%' }}>
            <span>Date range</span>
            <select
              id="dfx-range"
              value={daysBack}
              onChange={this.handleRangeChange}
              className="form-control"
            >
              {DATE_RANGES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </Col>
        <Col xs={12} sm={2}>
          <label htmlFor="dfx-location" style={{ width: '100%' }}>
            <span>Location</span>
            <input
              id="dfx-location"
              type="text"
              name="location"
              placeholder="Location ID"
              value={filters.location}
              onChange={this.handleFilterChange}
              className="form-control"
            />
          </label>
        </Col>
        <Col xs={12} sm={3}>
          <label htmlFor="dfx-driver" style={{ width: '100%' }}>
            <span>Driver</span>
            <input
              id="dfx-driver"
              type="text"
              name="driver"
              placeholder="email contains..."
              value={filters.driver}
              onChange={this.handleFilterChange}
              className="form-control"
            />
          </label>
        </Col>
        <Col xs={12} sm={3}>
          <label htmlFor="dfx-fixtype" style={{ width: '100%' }}>
            <span>Fix Type</span>
            <select
              id="dfx-fixtype"
              name="fixType"
              value={filters.fixType}
              onChange={this.handleFilterChange}
              className="form-control"
            >
              <option value="">All</option>
              {FIX_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </Col>
        <Col xs={12} sm={2}>
          <label htmlFor="dfx-outcome" style={{ width: '100%' }}>
            <span>Outcome</span>
            <select
              id="dfx-outcome"
              name="outcome"
              value={filters.outcome}
              onChange={this.handleFilterChange}
              className="form-control"
            >
              <option value="">All</option>
              {OUTCOME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </Col>
      </div>
    );
  }

  renderTable(rows) {
    const { expandedKeys, eventsByKey, workflowEventsByProcessId } = this.state;
    if (!rows.length) {
      return (
        <div style={{ padding: '2em', textAlign: 'center', color: '#888' }}>
          No DriverFix events found for the selected filters.
        </div>
      );
    }
    return (
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>When</th>
            <th>Location</th>
            <th>Driver</th>
            <th>Fix Type</th>
            <th>Outcome</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = !!expandedKeys[row.rowKey];
            return (
              <Fragment key={row.rowKey}>
                <tr
                  onClick={() => this.handleRowClick(row.rowKey)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ color: '#888', marginRight: '6px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    {formatDateTime(row.startMs)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {row.locationId ? (
                      <Link
                        to={`/locations/detail/${row.locationId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.locationId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{row.driver || '—'}</td>
                  <td>{getFixTypeLabel(row.fixType)}</td>
                  <td>
                    <OutcomePill outcome={row.outcome} />
                  </td>
                  <td>{row.duration}</td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      {renderTimeline(
                        timelineEventsForRow(
                          eventsByKey[row.rowKey],
                          workflowEventsByProcessId[row.stockingProcessId],
                        ),
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    );
  }

  render() {
    const { rows, isLoading, error } = this.state;
    const filtered = this.applyFilters(rows);
    return (
      <div className="driverFixesList">
        <PageHeader>DriverFix Events</PageHeader>
        {this.renderFilters()}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {isLoading ? <Loader /> : this.renderTable(filtered)}
      </div>
    );
  }
}
